import React from 'react';
import {
  Box,
  Heading,
  Flex,
  Text,
  Button,
  Input,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Checkbox,
  CheckboxGroup,
  Stack,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import useSWR from 'swr';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';

async function fetchSchoolConfig(data) {
  const { schoolId } = data;

  const [semRes, groupRes, programsRes] = await Promise.all([
    xhr(`/semesters?filters[school][id][$eq]=${schoolId}&sort[0]=name:asc&pagination[pageSize]=100&populate=grades,technical_careers,courses`),
    xhr(
      `/class-groups?filters[school][id][$eq]=${schoolId}&sort[0]=name:asc&pagination[pageSize]=100&populate=grade,technical_career,course`
    ),
    xhr(
      `/school-programs?filters[school][id][$eq]=${schoolId}&filters[active][$eq]=true&populate=program_type,grade,technical_career,course&pagination[pageSize]=200`
    ),
  ]);

  return {
    semesters: semRes.data?.data || [],
    groups: groupRes.data?.data || [],
    schoolPrograms: programsRes.data?.data || [],
  };
}

export default function SchoolConfiguracion() {
  const { user } = useAuth();
  const toast = useToast();
  const schoolId = user?.school?.id;

  const {
    isOpen: isSemOpen,
    onOpen: onSemOpen,
    onClose: onSemClose,
  } = useDisclosure();
  const {
    isOpen: isGroupOpen,
    onOpen: onGroupOpen,
    onClose: onGroupClose,
  } = useDisclosure();

  const [semName, setSemName] = React.useState('');
  const [semProgramType, setSemProgramType] = React.useState('');
  const [semPrograms, setSemPrograms] = React.useState([]);
  const [groupName, setGroupName] = React.useState('');
  const [groupProgramType, setGroupProgramType] = React.useState('');
  const [groupProgram, setGroupProgram] = React.useState('');
  const [creatingSem, setCreatingSem] = React.useState(false);
  const [creatingGroup, setCreatingGroup] = React.useState(false);

  const { data, mutate } = useSWR(
    { type: 'school-config', schoolId },
    fetchSchoolConfig,
    { revalidateOnFocus: false }
  );

  // Build programs list grouped by program type
  const { programTypeOptions, allProgramOptions } = React.useMemo(() => {
    if (!data?.schoolPrograms) return { programTypeOptions: [], allProgramOptions: [] };

    const typeMap = {};
    const options = [];

    data.schoolPrograms.forEach((sp) => {
      const attrs = sp.attributes || sp;
      const pt = attrs.program_type?.data || attrs.program_type;
      const ptId = pt?.id;
      const ptAttrs = pt?.attributes || pt;
      const grade = attrs.grade?.data || attrs.grade;
      const tc = attrs.technical_career?.data || attrs.technical_career;
      const course = attrs.course?.data || attrs.course;
      const program = grade || tc || course;
      const field = grade ? 'grade' : tc ? 'technical_career' : course ? 'course' : null;

      if (program && field && ptId) {
        const pAttrs = program.attributes || program;
        const key = `${field}-${program.id}`;
        const existing = options.find((o) => o.key === key);

        if (!existing) {
          options.push({
            key,
            id: program.id,
            name: pAttrs.name,
            field,
            programTypeId: ptId,
            programTypeName: ptAttrs?.name || 'Otros',
          });

          if (!typeMap[ptId]) {
            typeMap[ptId] = { id: ptId, name: ptAttrs?.name || 'Otros' };
          }
        }
      }
    });

    return {
      programTypeOptions: Object.values(typeMap),
      allProgramOptions: options,
    };
  }, [data?.schoolPrograms]);

  const filteredSemProgramOptions = React.useMemo(() => {
    if (!semProgramType) return [];
    return allProgramOptions.filter(
      (o) => String(o.programTypeId) === String(semProgramType)
    );
  }, [semProgramType, allProgramOptions]);

  const filteredGroupProgramOptions = React.useMemo(() => {
    if (!groupProgramType) return [];
    return allProgramOptions.filter(
      (o) => String(o.programTypeId) === String(groupProgramType)
    );
  }, [groupProgramType, allProgramOptions]);

  const resetSemForm = () => {
    setSemName('');
    setSemProgramType('');
    setSemPrograms([]);
  };

  const resetGroupForm = () => {
    setGroupName('');
    setGroupProgramType('');
    setGroupProgram('');
  };

  const handleCreateSemester = async () => {
    if (!semName.trim()) return;
    try {
      setCreatingSem(true);

      const payload = {
        name: semName.trim(),
        school: schoolId,
        publishedAt: new Date().toISOString(),
      };

      // Group selected programs by field type (grades, technical_careers, courses)
      semPrograms.forEach((key) => {
        const opt = allProgramOptions.find((o) => o.key === key);
        if (opt) {
          // Semester uses plural relation names: grades, technical_careers, courses
          const pluralField =
            opt.field === 'grade'
              ? 'grades'
              : opt.field === 'technical_career'
              ? 'technical_careers'
              : 'courses';
          if (!payload[pluralField]) payload[pluralField] = [];
          payload[pluralField].push(opt.id);
        }
      });

      await xhr.post('/semesters', { data: payload });
      toast({
        title: 'Periodo creado',
        status: 'success',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });
      setSemName('');
      setSemProgramType('');
      setSemPrograms([]);
      onSemClose();
      mutate();
    } catch (error) {
      toast({
        title: 'Error al crear periodo',
        description: error.response?.data?.error?.message || 'Intenta de nuevo',
        status: 'error',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setCreatingSem(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) return;
    try {
      setCreatingGroup(true);

      const payload = {
        name: groupName.trim(),
        school: schoolId,
        active: true,
        publishedAt: new Date().toISOString(),
      };

      // Associate the group to the selected program
      if (groupProgram) {
        const selected = allProgramOptions.find((o) => o.key === groupProgram);
        if (selected) {
          payload[selected.field] = selected.id;
        }
      }

      await xhr.post('/class-groups', { data: payload });
      toast({
        title: 'Grupo creado',
        status: 'success',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });
      setGroupName('');
      setGroupProgramType('');
      setGroupProgram('');
      onGroupClose();
      mutate();
    } catch (error) {
      toast({
        title: 'Error al crear grupo',
        description: error.response?.data?.error?.message || 'Intenta de nuevo',
        status: 'error',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setCreatingGroup(false);
    }
  };

  const getGroupProgramName = (group) => {
    const attrs = group.attributes || group;
    const grade = attrs.grade?.data?.attributes || attrs.grade;
    const tc = attrs.technical_career?.data?.attributes || attrs.technical_career;
    const course = attrs.course?.data?.attributes || attrs.course;
    return grade?.name || tc?.name || course?.name || '-';
  };

  if (!data) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  const { semesters, groups } = data;

  return (
    <Box>
      <Heading size="lg" mb={6}>
        Configuracion
      </Heading>

      {/* Periodos academicos */}
      <Box
        bg="white"
        borderRadius="xl"
        border="1px solid"
        borderColor="gray.200"
        p={5}
        mb={6}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Text fontWeight="600" fontSize="md">
              Periodos academicos
            </Text>
            <Text fontSize="sm" color="gray.500">
              Define los periodos academicos de tu institucion
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            size="sm"
            colorScheme="purple"
            onClick={onSemOpen}
          >
            Crear periodo
          </Button>
        </Flex>

        {semesters.length === 0 ? (
          <Box
            textAlign="center"
            py={8}
            color="gray.400"
            border="1px dashed"
            borderColor="gray.200"
            borderRadius="lg"
          >
            <Text fontSize="sm">No hay periodos creados</Text>
            <Text fontSize="xs" mt={1}>
              Crea al menos un periodo para poder asignarlo a los estudiantes
            </Text>
          </Box>
        ) : (
          <TableContainer>
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Nombre</Th>
                  <Th>Programas</Th>
                  <Th textAlign="right">Fecha creacion</Th>
                </Tr>
              </Thead>
              <Tbody>
                {semesters.map((sem) => {
                  const attrs = sem.attributes || sem;
                  const semGrades = (attrs.grades?.data || attrs.grades || []).map(
                    (g) => (g.attributes || g).name
                  );
                  const semTc = (attrs.technical_careers?.data || attrs.technical_careers || []).map(
                    (t) => (t.attributes || t).name
                  );
                  const semCourses = (attrs.courses?.data || attrs.courses || []).map(
                    (c) => (c.attributes || c).name
                  );
                  const allPrograms = [...semGrades, ...semTc, ...semCourses];

                  return (
                    <Tr key={sem.id}>
                      <Td fontWeight="500">{attrs.name}</Td>
                      <Td fontSize="sm" color="gray.600">
                        {allPrograms.length > 0 ? allPrograms.join(', ') : '-'}
                      </Td>
                      <Td textAlign="right" fontSize="sm" color="gray.500">
                        {attrs.createdAt
                          ? new Date(attrs.createdAt).toLocaleDateString()
                          : '-'}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Grupos */}
      <Box
        bg="white"
        borderRadius="xl"
        border="1px solid"
        borderColor="gray.200"
        p={5}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Text fontWeight="600" fontSize="md">
              Grupos
            </Text>
            <Text fontSize="sm" color="gray.500">
              Grupos de clase para organizar a tus estudiantes
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            size="sm"
            colorScheme="purple"
            onClick={onGroupOpen}
          >
            Crear grupo
          </Button>
        </Flex>

        {groups.length === 0 ? (
          <Box
            textAlign="center"
            py={8}
            color="gray.400"
            border="1px dashed"
            borderColor="gray.200"
            borderRadius="lg"
          >
            <Text fontSize="sm">No hay grupos creados</Text>
            <Text fontSize="xs" mt={1}>
              Crea al menos un grupo para poder asignarlo a los estudiantes
            </Text>
          </Box>
        ) : (
          <TableContainer>
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Nombre</Th>
                  <Th>Programa</Th>
                  <Th textAlign="right">Estado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {groups.map((g) => {
                  const attrs = g.attributes || g;
                  return (
                    <Tr key={g.id}>
                      <Td fontWeight="500">{attrs.name}</Td>
                      <Td fontSize="sm" color="gray.600">
                        {getGroupProgramName(g)}
                      </Td>
                      <Td textAlign="right">
                        <Badge
                          colorScheme={attrs.active !== false ? 'green' : 'gray'}
                          borderRadius="full"
                          px={2}
                        >
                          {attrs.active !== false ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Modal: Crear Semestre */}
      <Modal
        isOpen={isSemOpen}
        onClose={() => {
          resetSemForm();
          onSemClose();
        }}
        size="md"
      >
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Crear periodo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired mb={4}>
              <FormLabel>Nombre del periodo</FormLabel>
              <Input
                value={semName}
                onChange={(e) => setSemName(e.target.value)}
                placeholder="Ej: 2026-1, Primer periodo 2026"
              />
            </FormControl>
            {programTypeOptions.length > 0 && (
              <>
                <FormControl mb={4}>
                  <FormLabel>Tipo de programa</FormLabel>
                  <Select
                    placeholder="Seleccionar tipo"
                    value={semProgramType}
                    onChange={(e) => setSemProgramType(e.target.value)}
                  >
                    {programTypeOptions.map((pt) => (
                      <option key={pt.id} value={pt.id}>
                        {pt.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                {semProgramType && filteredSemProgramOptions.length > 0 && (
                  <FormControl>
                    <FormLabel>Programas asociados</FormLabel>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Selecciona los programas a los que aplica este periodo
                    </Text>
                    <CheckboxGroup value={semPrograms} onChange={setSemPrograms}>
                      <Stack spacing={2}>
                        {filteredSemProgramOptions.map((o) => (
                          <Checkbox key={o.key} value={o.key}>
                            {o.name}
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  </FormControl>
                )}
                {semProgramType && filteredSemProgramOptions.length === 0 && (
                  <Text fontSize="sm" color="gray.500">
                    No hay programas habilitados para este tipo
                  </Text>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                resetSemForm();
                onSemClose();
              }}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleCreateSemester}
              isLoading={creatingSem}
            >
              Crear
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: Crear Grupo */}
      <Modal
        isOpen={isGroupOpen}
        onClose={() => {
          resetGroupForm();
          onGroupClose();
        }}
        size="md"
      >
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Crear grupo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired mb={4}>
              <FormLabel>Nombre del grupo</FormLabel>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Ej: Grupo A, 11-A, Jornada Manana"
              />
            </FormControl>
            {programTypeOptions.length > 0 && (
              <>
                <FormControl mb={4}>
                  <FormLabel>Tipo de programa</FormLabel>
                  <Select
                    placeholder="Seleccionar tipo"
                    value={groupProgramType}
                    onChange={(e) => {
                      setGroupProgramType(e.target.value);
                      setGroupProgram('');
                    }}
                  >
                    {programTypeOptions.map((pt) => (
                      <option key={pt.id} value={pt.id}>
                        {pt.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Programa</FormLabel>
                  <Select
                    placeholder="Seleccionar programa"
                    value={groupProgram}
                    onChange={(e) => setGroupProgram(e.target.value)}
                    isDisabled={!groupProgramType}
                  >
                    {filteredGroupProgramOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.name}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Asocia el grupo a un programa para que aparezca al asignar estudiantes
                  </Text>
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                resetGroupForm();
                onGroupClose();
              }}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleCreateGroup}
              isLoading={creatingGroup}
            >
              Crear
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
